import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { XMarkIcon, CameraIcon, QrCodeIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface SingleTagQRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTagConfirmed: (tag: string) => void;
  title?: string;
}

const SingleTagQRScannerModal: React.FC<SingleTagQRScannerModalProps> = ({ 
  isOpen, 
  onClose, 
  onTagConfirmed,
  title = "Scan RFID Tag"
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedTag, setScannedTag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  const stopCamera = useCallback(() => {
    setIsScanning(false);
    setCameraReady(false);
    setError(null);
    
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const scanVideoFrame = useCallback(() => {
    if (!isScanning || !videoRef.current || !canvasRef.current || !cameraReady || showConfirmation) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    
    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(scanVideoFrame);
      return;
    }

    // Full resolution for accurate scanning
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Scan central area
    const scanRatio = 0.4;
    const scanSize = Math.min(canvas.width, canvas.height) * scanRatio;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    const scanArea = {
      x: Math.floor(centerX - scanSize / 2),
      y: Math.floor(centerY - scanSize / 2),
      width: Math.ceil(scanSize),
      height: Math.ceil(scanSize)
    };
    
    // Ensure scan area is within bounds
    scanArea.x = Math.max(0, scanArea.x);
    scanArea.y = Math.max(0, scanArea.y);
    scanArea.width = Math.min(scanArea.width, canvas.width - scanArea.x);
    scanArea.height = Math.min(scanArea.height, canvas.height - scanArea.y);
    
    try {
      const imageData = context.getImageData(
        scanArea.x, 
        scanArea.y, 
        scanArea.width, 
        scanArea.height
      );
      
      const code = jsQR(imageData.data, scanArea.width, scanArea.height, {
        inversionAttempts: "dontInvert"
      });
      
      if (code && code.data) {
        const cleanTag = code.data.trim().toUpperCase();
        if (cleanTag.length === 12) {
          // Tag found! Stop scanning and show confirmation
          setScannedTag(cleanTag);
          setShowConfirmation(true);
          setIsScanning(false);
          
          // Vibrate if available
          if ('vibrate' in navigator) {
            navigator.vibrate(100);
          }
          
          return; // Stop scanning
        }
      }
    } catch (e) {
      console.error('QR scanning error:', e);
    }
    
    // Continue scanning
    animationFrameRef.current = requestAnimationFrame(scanVideoFrame);
  }, [isScanning, cameraReady, showConfirmation]);

  const startCamera = async () => {
    try {
      setError(null);
      setShowConfirmation(false);
      setScannedTag(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 30, max: 30 }
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                setCameraReady(true);
                setIsScanning(true);
              })
              .catch(() => {
                setCameraReady(true);
                setIsScanning(true);
              });
          }
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  // Start scanning when ready
  useEffect(() => {
    if (isScanning && cameraReady && !showConfirmation) {
      scanVideoFrame();
    }
  }, [isScanning, cameraReady, showConfirmation, scanVideoFrame]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setScannedTag(null);
      setShowConfirmation(false);
    }
  }, [isOpen, stopCamera]);

  const handleConfirm = () => {
    if (scannedTag) {
      onTagConfirmed(scannedTag);
      handleClose();
    }
  };

  const handleRescan = () => {
    setShowConfirmation(false);
    setScannedTag(null);
    setIsScanning(true);
    // Camera is still active, just resume scanning
    scanVideoFrame();
  };

  const handleClose = () => {
    stopCamera();
    setScannedTag(null);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 9999,
        display: 'block',
        width: '100%',
        height: '100%'
      }}
    >
      {/* Header */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e5e5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px'
        }}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{title}</h3>
        <button
          onClick={handleClose}
          style={{
            padding: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          <XMarkIcon style={{ width: '24px', height: '24px' }} />
        </button>
      </div>

      {/* Main content area */}
      <div 
        style={{
          position: 'absolute',
          top: '60px',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {error && (
          <div 
            style={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              color: '#c00',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}

        {!showConfirmation ? (
          <>
            {/* Camera container */}
            <div 
              style={{
                position: 'relative',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '16px',
                width: '100%',
                maxWidth: '400px',
                height: '300px'
              }}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: cameraReady ? 'block' : 'none'
                }}
              />
              
              {/* Scanning area overlay */}
              {cameraReady && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 10
                  }}
                >
                  {/* Scanning square in center */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '180px',
                      height: '180px',
                      transform: 'translate(-50%, -50%)',
                      border: '3px solid #16a34a',
                      borderRadius: '12px',
                      boxShadow: '0 0 0 2px rgba(22, 163, 74, 0.3), inset 0 0 0 2px rgba(22, 163, 74, 0.3)',
                      animation: isScanning ? 'pulse 2s infinite' : 'none'
                    }}
                  />
                  
                  {/* Corner indicators */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '180px',
                      height: '180px',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    {/* Top-left corner */}
                    <div style={{
                      position: 'absolute',
                      top: '-3px',
                      left: '-3px',
                      width: '30px',
                      height: '30px',
                      borderTop: '6px solid #16a34a',
                      borderLeft: '6px solid #16a34a',
                      borderRadius: '12px 0 0 0'
                    }} />
                    
                    {/* Top-right corner */}
                    <div style={{
                      position: 'absolute',
                      top: '-3px',
                      right: '-3px',
                      width: '30px',
                      height: '30px',
                      borderTop: '6px solid #16a34a',
                      borderRight: '6px solid #16a34a',
                      borderRadius: '0 12px 0 0'
                    }} />
                    
                    {/* Bottom-left corner */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-3px',
                      left: '-3px',
                      width: '30px',
                      height: '30px',
                      borderBottom: '6px solid #16a34a',
                      borderLeft: '6px solid #16a34a',
                      borderRadius: '0 0 0 12px'
                    }} />
                    
                    {/* Bottom-right corner */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-3px',
                      right: '-3px',
                      width: '30px',
                      height: '30px',
                      borderBottom: '6px solid #16a34a',
                      borderRight: '6px solid #16a34a',
                      borderRadius: '0 0 12px 0'
                    }} />
                  </div>
                  
                  {/* Instruction text */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Center QR code in square
                  </div>
                </div>
              )}
              
              {/* Hidden canvas for QR processing */}
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
              
              {!cameraReady && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}
                >
                  <CameraIcon style={{ width: '48px', height: '48px', color: '#999', margin: '0 auto 8px' }} />
                  <p style={{ color: '#666', margin: '0' }}>Camera Preview</p>
                  {!isScanning && (
                    <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                      Tap "Start Camera" to begin
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Camera button */}
            <button
              onClick={isScanning ? stopCamera : startCamera}
              disabled={!!error}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '12px 16px',
                borderRadius: '8px',
                fontWeight: '500',
                color: 'white',
                border: 'none',
                backgroundColor: isScanning ? '#dc2626' : '#2563eb',
                opacity: error ? 0.5 : 1,
                cursor: error ? 'not-allowed' : 'pointer'
              }}
            >
              {isScanning ? 'Stop Camera' : 'Start Camera'}
            </button>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Position the QR code within the green square
              </p>
              <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                The scanner will automatically detect the tag
              </p>
            </div>
          </>
        ) : (
          /* Confirmation screen */
          <div 
            style={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              padding: '24px',
              marginTop: '40px'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <CheckCircleIcon 
                style={{ 
                  width: '64px', 
                  height: '64px', 
                  color: '#16a34a', 
                  margin: '0 auto 16px' 
                }} 
              />
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#111827',
                marginBottom: '8px'
              }}>
                Tag Scanned Successfully
              </h3>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                Please confirm this is the correct tag
              </p>
            </div>

            <div 
              style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}
            >
              <p style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                RFID Tag Number
              </p>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: '600', 
                fontFamily: 'monospace',
                color: '#111827',
                letterSpacing: '0.1em'
              }}>
                {scannedTag}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleRescan}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <ArrowPathIcon style={{ width: '20px', height: '20px' }} />
                Rescan
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  color: 'white',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <CheckCircleIcon style={{ width: '20px', height: '20px' }} />
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default SingleTagQRScannerModal;